import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'motion/react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { JOIN_FORM_URL } from '../config/links';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Animated Section Component
function AnimatedSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Pull Quote Component
function PullQuote({ children }: { children: React.ReactNode }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.blockquote
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative my-16 py-8 px-8 md:px-16 border-l-4 border-[#ffcc10] bg-gradient-to-r from-[#ffcc10]/5 to-transparent"
    >
      <p className="font-['DM_Serif_Display',serif] text-2xl md:text-3xl lg:text-4xl text-[#050e1d] leading-relaxed italic">
        {children}
      </p>
    </motion.blockquote>
  );
}

// Criteria Item Component
function CriteriaItem({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className="flex items-start gap-3 mb-4"
    >
      <CheckCircle2 className="w-6 h-6 text-[#1e8eaf] mt-1 flex-shrink-0" />
      <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 leading-relaxed">
        {children}
      </p>
    </motion.div>
  );
}

// Question Card Component
function QuestionCard({ number, question, description }: { number: number; question: string; description: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group"
    >
      <motion.div
        animate={{ 
          borderColor: isHovered ? '#1e8eaf' : 'rgba(5,14,26,0.1)',
          boxShadow: isHovered ? '0 10px 40px rgba(30,142,175,0.1)' : '0 2px 10px rgba(5,14,26,0.05)'
        }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl p-8 border-2"
      >
        <div className="flex items-start gap-4 mb-4">
          <motion.div
            animate={{ 
              backgroundColor: isHovered ? '#1e8eaf' : '#184b9d',
              scale: isHovered ? 1.1 : 1
            }}
            transition={{ duration: 0.3 }}
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          >
            <span className="font-['DM_Serif_Display',serif] text-2xl text-white">{number}</span>
          </motion.div>
          <div className="flex-1">
            <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-3">
              {question}
            </h3>
            <p className="font-['Manrope',sans-serif] text-[#050e1d]/70 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Join() {
  const heroRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SEO
        title="Join Unpatterned - Application Process"
        description="Join a network of thoughtful people committed to building cognitive depth. Learn about our application process and community values."
        path="/join"
      />
      
      {/* Progress Bar */}
      <motion.div
        style={{ scaleX: scrollProgress / 100 }}
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#184b9d] via-[#1e8eaf] to-[#ffcc10] origin-left z-[60]"
      />

      <Header />

      <main className="bg-white pt-20 md:pt-24">
        {/* Hero Section */}
        <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-b from-[#184b9d]/5 via-white to-white py-20 md:py-32">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-center"
            >
              <motion.h1
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="font-['DM_Serif_Display',serif] text-5xl md:text-6xl lg:text-7xl text-[#050e1d] mb-8 leading-tight"
              >
                Joining Unpatterned
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-['Manrope',sans-serif] text-xl md:text-2xl text-[#050e1d]/70 mb-4"
              >
                Entry Criteria & Selection Process
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Opening Statement */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <AnimatedSection>
              <div className="text-center mb-12">
                <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-8 leading-tight">
                  Unpatterned is open — <br className="hidden md:block" />
                  <span className="text-[#184b9d]">but it is not casual.</span>
                </h2>
                <p className="font-['Manrope',sans-serif] text-xl md:text-2xl text-[#050e1d]/80 leading-relaxed max-w-3xl mx-auto">
                  We are building a network of people who care deeply about thinking carefully. That requires intention.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection className="mt-16">
              <div className="bg-gradient-to-br from-[#184b9d]/5 to-[#1e8eaf]/5 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-6">
                      You do not need:
                    </h3>
                    <ul className="space-y-4">
                      <li className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 pl-6 border-l-2 border-[#050e1d]/20">
                        Credentials
                      </li>
                      <li className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 pl-6 border-l-2 border-[#050e1d]/20">
                        Institutional affiliation
                      </li>
                      <li className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 pl-6 border-l-2 border-[#050e1d]/20">
                        To be an expert
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#184b9d] mb-6">
                      You do need:
                    </h3>
                    <ul className="space-y-4">
                      <li className="font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d] pl-6 border-l-4 border-[#ffcc10]">
                        Curiosity
                      </li>
                      <li className="font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d] pl-6 border-l-4 border-[#ffcc10]">
                        Discipline
                      </li>
                      <li className="font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d] pl-6 border-l-4 border-[#ffcc10]">
                        Respect for complexity
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeInOut" }}
            className="h-px bg-gradient-to-r from-transparent via-[#184b9d]/30 to-transparent origin-center"
          />
        </div>

        {/* Application Questions */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <AnimatedSection className="mb-16">
              <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6 leading-tight">
                What We Ask in the Application
              </h2>
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed">
                We keep the application simple and human. We want to understand how you think — not what you have achieved.
              </p>
            </AnimatedSection>

            <AnimatedSection className="mb-12">
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 mb-12">
                Applicants are invited to reflect on:
              </p>
            </AnimatedSection>

            <div className="space-y-8">
              <QuestionCard
                number={1}
                question="Why do you want to join Unpatterned?"
                description="What draws you toward this kind of intellectual practice?"
              />
              <QuestionCard
                number={2}
                question="Describe a complex problem you've thought deeply about."
                description="It can be technical, social, philosophical, or personal. We care less about the topic and more about how you approached it."
              />
              <QuestionCard
                number={3}
                question="What are you trying to understand better this year?"
                description="Where is your intellectual curiosity pulling you?"
              />
              <QuestionCard
                number={4}
                question="What kind of contribution would you like to make?"
                description="Discussion, writing, building, connecting people, research — leave room for creativity."
              />
              <QuestionCard
                number={5}
                question="How much time can you realistically commit each month?"
                description="Depth requires rhythm. We want honesty, not aspiration."
              />
            </div>
          </div>
        </section>

        {/* What We Look For */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-white to-[#184b9d]/5">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <AnimatedSection className="mb-16">
              <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6 leading-tight">
                What We Look For
              </h2>
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/70 leading-relaxed mb-12">
                We do not select based on résumé, title, or geography.
              </p>
            </AnimatedSection>

            <AnimatedSection>
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mb-12"
              >
                <CriteriaItem delay={0}>Seriousness of intention</CriteriaItem>
                <CriteriaItem delay={0.1}>Creativity in thought</CriteriaItem>
                <CriteriaItem delay={0.2}>Clarity of reasoning</CriteriaItem>
                <CriteriaItem delay={0.3}>Intellectual humility</CriteriaItem>
                <CriteriaItem delay={0.4}>Willingness to be challenged</CriteriaItem>
                <CriteriaItem delay={0.5}>Commitment to contributing, not just consuming</CriteriaItem>
              </motion.div>
            </AnimatedSection>

            <PullQuote>
              Thoughtful people are not defined by prestige. They are defined by how they engage complexity.
            </PullQuote>
          </div>
        </section>

        {/* The Next Stage */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <AnimatedSection className="mb-16">
              <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6 leading-tight">
                The Next Stage
              </h2>
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed mb-8">
                Shortlisted applicants are invited to a conversation.
              </p>
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 leading-relaxed">
                This is not an interview in the traditional sense. It is a dialogue.
              </p>
            </AnimatedSection>

            <AnimatedSection className="mb-12">
              <div className="bg-white rounded-3xl border-2 border-[#1e8eaf]/20 p-8 md:p-12 shadow-xl">
                <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-8">
                  We want to understand:
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#1e8eaf] mt-2" />
                    <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                      How you think in real time
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#1e8eaf] mt-2" />
                    <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                      How you respond to uncertainty
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#1e8eaf] mt-2" />
                    <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                      How you listen
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-[#1e8eaf] mt-2" />
                    <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80">
                      How you refine your own ideas
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="mb-12">
              <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 leading-relaxed">
                You may also be given a short concept or problem to reflect on before the conversation — not to "get it right," but to observe how you structure your thinking.
              </p>
            </AnimatedSection>

            <AnimatedSection className="mb-12">
              <div className="bg-gradient-to-br from-[#ffcc10]/10 to-[#ffcc10]/5 rounded-3xl p-8 md:p-12 border border-[#ffcc10]/30">
                <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-8">
                  This stage ensures cultural alignment.
                </h3>
                <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/80 mb-6">
                  Unpatterned values:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#184b9d] mt-1 flex-shrink-0" />
                    <p className="font-['Manrope',sans-serif] text-[#050e1d]/80">
                      Argument without ego
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#184b9d] mt-1 flex-shrink-0" />
                    <p className="font-['Manrope',sans-serif] text-[#050e1d]/80">
                      Depth over speed
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#184b9d] mt-1 flex-shrink-0" />
                    <p className="font-['Manrope',sans-serif] text-[#050e1d]/80">
                      Openness to correction
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#184b9d] mt-1 flex-shrink-0" />
                    <p className="font-['Manrope',sans-serif] text-[#050e1d]/80">
                      Care for the impact of ideas
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <PullQuote>
              We are building a high-trust network. Trust is formed through shared standards of thought.
            </PullQuote>
          </div>
        </section>

        {/* Cultural Signal */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-[#184b9d]/5 to-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <AnimatedSection className="mb-16">
              <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl text-[#050e1d] mb-6 leading-tight">
                Cultural Signal
              </h2>
              <p className="font-['Manrope',sans-serif] text-xl text-[#050e1d]/80 leading-relaxed mb-4">
                Unpatterned is not competitive in spirit.
              </p>
              <p className="font-['Manrope',sans-serif] text-xl text-[#184b9d] font-semibold leading-relaxed">
                It is disciplined in practice.
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <AnimatedSection>
                <div className="bg-white rounded-3xl p-8 border-2 border-[#1e8eaf] h-full">
                  <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#1e8eaf] mb-6">
                    If you enjoy:
                  </h3>
                  <ul className="space-y-4">
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/80 pl-4 border-l-2 border-[#1e8eaf]/30">
                      Wrestling with ideas
                    </li>
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/80 pl-4 border-l-2 border-[#1e8eaf]/30">
                      Sitting with difficult questions
                    </li>
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/80 pl-4 border-l-2 border-[#1e8eaf]/30">
                      Being corrected without defensiveness
                    </li>
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/80 pl-4 border-l-2 border-[#1e8eaf]/30">
                      Helping others think better
                    </li>
                  </ul>
                  <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d] mt-8 font-semibold">
                    You will feel at home here.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection>
                <div className="bg-[#050e1d]/5 rounded-3xl p-8 border-2 border-[#050e1d]/20 h-full">
                  <h3 className="font-['DM_Serif_Display',serif] text-2xl text-[#050e1d] mb-6">
                    If you are looking for:
                  </h3>
                  <ul className="space-y-4">
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/60 pl-4 border-l-2 border-[#050e1d]/20">
                      Validation
                    </li>
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/60 pl-4 border-l-2 border-[#050e1d]/20">
                      Status
                    </li>
                    <li className="font-['Manrope',sans-serif] text-[#050e1d]/60 pl-4 border-l-2 border-[#050e1d]/20">
                      Passive learning
                    </li>
                  </ul>
                  <p className="font-['Manrope',sans-serif] text-lg text-[#050e1d]/70 mt-8">
                    This may not be the right space.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-[#184b9d] via-[#1e8eaf] to-[#184b9d]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['DM_Serif_Display',serif] text-4xl md:text-5xl lg:text-6xl text-white mb-8 leading-tight">
                Ready to Begin?
              </h2>
              <p className="font-['Manrope',sans-serif] text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-2xl mx-auto">
                Take your time with your responses — we're looking for depth, not speed.
              </p>
              <motion.a
                href={JOIN_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-3 bg-[#ffcc10] text-[#050e1d] px-10 py-5 rounded-xl font-['Manrope',sans-serif] font-semibold text-lg hover:bg-[#ffcc10]/90 transition-all shadow-2xl"
              >
                Submit Your Application
                <ArrowRight className="w-6 h-6" />
              </motion.a>
              <p className="font-['Manrope',sans-serif] text-white/70 mt-8 text-sm">
                Questions? Reach out to <a href="mailto:connect@unpatterned.org" className="underline hover:text-white">connect@unpatterned.org</a>
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
